import { editorUrl } from '@/lib/editorBasePath';
import { useState, useEffect, useRef } from 'react';
import { YearbookPage, YearbookElement, PageBackground, setPageDimensions, DEFAULT_PAGE_WIDTH, DEFAULT_PAGE_HEIGHT } from '@/types/yearbook';
import { normalizeElements } from '@/lib/normalizeElements';
import { setUploadSocialNetworkId } from '@/lib/directUpload';

interface BookPageEntry {
  id: number;
  page_id: number;
}

interface ApiPageResponse {
  id?: number;
  page_id: number;
  item_type?: string;
  named?: string | null;
  note?: string | null;
  objects_positions: {
    elements: any[];
    background: PageBackground;
    guides?: any[];
  };
}

export interface AllFolder {
  id: number;
  name?: string;
  title: string; // normalized from `name` at load time
  pictures_count?: number;
}

export interface TrombiTemplate {
  id: number;
  personn_per_page: number;
}

export type TrombiTemplatesByStyle = Record<string, TrombiTemplate[]>;

export interface TrombiStyleInfo {
  name: string;
  preview: string;
}

export type TrombiInfosByStyle = Record<string, TrombiStyleInfo>;

export interface TrameTemplateStyleInfo {
  name: string;
}

export type TrameTemplateInfosByStyle = Record<string, TrameTemplateStyleInfo>;

export interface TrameBackgroundStyleInfo {
  name: string;
}

export type TrameBackgroundInfosByStyle = Record<string, TrameBackgroundStyleInfo>;

export interface StickerStyleInfo {
  name: string;
}

export type StickerInfosByStyle = Record<string, StickerStyleInfo>;

export interface CoverStyleInfo {
  name: string;
}

export type CoverInfosByStyle = Record<string, CoverStyleInfo>;

interface LoadBookResponse {
  pages: BookPageEntry[];
  initial_pages: ApiPageResponse[];
  page_width?: number;
  page_height?: number;
  cover_front?: ApiPageResponse;
  cover_back?: ApiPageResponse;
  cover_tranche?: ApiPageResponse;
  tranche_height?: number;
  trame_template_infos_style?: TrameTemplateInfosByStyle;
  trame_background_infos_style?: TrameBackgroundInfosByStyle;
  sticker_infos_style?: StickerInfosByStyle;
  cover_infos_style?: CoverInfosByStyle;
  cover_name?: string;
  favorites_colors?: string[];
  social_network_name?: string;
  picture_to_use?: number[];
  all_folders?: AllFolder[];
  yearbook_quantity?: number;
  with_trombi?: boolean;
  plateforme?: string;
  yearbook_id?: number;
  default_font?: string;
  only_page_id?: number[];
  role?: string;
  format_diplay?: string;
  price_with_promo_display?: string;
  price_without_promo_display?: string;
  format_of_yearbook?: string;
  current_format?: string;
  social_network_id?: number;
  pictures_count?: number;
}

const FALLBACK_PAGE_COUNT = 32;

function createEmptyPage(id: number, pageNumber: number): YearbookPage {
  return {
    id: String(id),
    pageNumber,
    elements: [],
    background: { type: 'color' as const, value: '#ffffff' },
  };
}

function parseCoverPage(raw: ApiPageResponse, coverType: 'front' | 'back' | 'tranche', pageWidth?: number): YearbookPage {
  let elements = normalizeElements(raw.objects_positions?.elements ?? []);
  // Tranche: lock all elements so users can only edit style/content, not move/resize/rotate.
  if (coverType === 'tranche') {
    elements = elements.map((el) => ({ ...el, locked: true }));
  }
  return {
    id: String(raw.id ?? raw.page_id),
    pageNumber: raw.page_id,
    elements,
    background: raw.objects_positions?.background ?? { type: 'color' as const, value: '#ffffff' },
    guides: raw.objects_positions?.guides ?? [],
    noteToGraphiste: raw.note || undefined,
    coverType,
    ...(pageWidth ? { pageWidth } : {}),
  };
}

function parseInitialPages(raw: ApiPageResponse[]): YearbookPage[] {
  return raw.map((item) => {
    try {
      return {
        id: String(item.id ?? item.page_id),
        pageNumber: item.page_id,
        elements: normalizeElements(item.objects_positions?.elements ?? []),
        background: item.objects_positions?.background ?? { type: 'color' as const, value: '#ffffff' },
        guides: item.objects_positions?.guides ?? [],
        name: item.named || null,
        noteToGraphiste: item.note || undefined,
        itemType: item.item_type || undefined,
      };
    } catch {
      return createEmptyPage(item.id ?? item.page_id, item.page_id ?? 0);
    }
  });
}

export function useBookLoader() {
  const [bookPages, setBookPages] = useState<YearbookPage[] | null>(null);
  const [initialLoadedIndices, setInitialLoadedIndices] = useState<Set<number>>(new Set());
  const [trameTemplateInfos, setTrameTemplateInfos] = useState<TrameTemplateInfosByStyle>({});
  const [trameBackgroundInfos, setTrameBackgroundInfos] = useState<TrameBackgroundInfosByStyle>({});
  const [stickerInfos, setStickerInfos] = useState<StickerInfosByStyle>({});
  const [coverInfos, setCoverInfos] = useState<CoverInfosByStyle>({});
  const [coverName, setCoverName] = useState<string>('');
  const [favoritesColors, setFavoritesColors] = useState<string[]>([]);
  const [socialNetworkName, setSocialNetworkName] = useState<string | null>(null);
  const [pictureToUse, setPictureToUse] = useState<Set<number>>(new Set());
  const [allFolders, setAllFolders] = useState<AllFolder[]>([]);
  const [yearbookQuantity, setYearbookQuantity] = useState<number>(0);
  const [withTrombi, setWithTrombi] = useState<boolean>(true);
  const [plateforme, setPlateforme] = useState<string>('');
  const [yearbookId, setYearbookId] = useState<number | null>(null);
  const [defaultFont, setDefaultFont] = useState<string>('DM Sans');
  const [lockedPageNumbers, setLockedPageNumbers] = useState<Set<number>>(new Set());
  const [onlyPageIds, setOnlyPageIds] = useState<number[]>([]);
  const [role, setRole] = useState<string>('');
  const [formatDisplay, setFormatDisplay] = useState<string>('');
  const [priceWithPromoDisplay, setPriceWithPromoDisplay] = useState<string>('');
  const [priceWithoutPromoDisplay, setPriceWithoutPromoDisplay] = useState<string>('');
  const [formatOfYearbook, setFormatOfYearbook] = useState<string>('');
  const [currentFormat, setCurrentFormat] = useState<string>('');
  const [socialNetworkId, setSocialNetworkId] = useState<number | null>(null);
  const [picturesCount, setPicturesCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const didFetch = useRef(false);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;

    fetch(editorUrl('/load_book'))
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: LoadBookResponse) => {
        if (!data?.pages || !Array.isArray(data.pages) || data.pages.length === 0) {
          throw new Error('Invalid book data');
        }

        // Apply page dimensions from backend (or keep defaults)
        setPageDimensions(
          data.page_width ?? DEFAULT_PAGE_WIDTH,
          data.page_height ?? DEFAULT_PAGE_HEIGHT
        );

        // Build skeleton pages from the page list
        const skeleton: YearbookPage[] = data.pages.map((entry) =>
          createEmptyPage(entry.id, entry.page_id)
        );

        // Parse pre-loaded initial pages and merge them in
        const preloaded = parseInitialPages(data.initial_pages ?? []);
        const loadedSet = new Set<number>();

        for (const loaded of preloaded) {
          const idx = skeleton.findIndex((p) => p.pageNumber === loaded.pageNumber);
          if (idx !== -1) {
            skeleton[idx] = loaded;
            loadedSet.add(idx);
          }
        }

        // Parse covers and prepend them: [back_cover, (tranche), front_cover, ...pages]
        const covers: YearbookPage[] = [];
        if (data.cover_back) {
          covers.push(parseCoverPage(data.cover_back, 'back'));
        }
        if (data.cover_tranche && data.tranche_height && data.tranche_height >= 14.1732) {
          covers.push(parseCoverPage(data.cover_tranche, 'tranche', data.tranche_height));
        }
        if (data.cover_front) {
          covers.push(parseCoverPage(data.cover_front, 'front'));
        }

        // Shift loaded indices to account for cover pages prepended
        const shiftedSet = new Set<number>();
        loadedSet.forEach((i) => shiftedSet.add(i + covers.length));
        // Covers are always loaded
        covers.forEach((_, i) => shiftedSet.add(i));

        const allPages = [...covers, ...skeleton];

        setBookPages(allPages);
        setInitialLoadedIndices(shiftedSet);
        setTrameTemplateInfos(data.trame_template_infos_style ?? {});
        setTrameBackgroundInfos(data.trame_background_infos_style ?? {});
        setStickerInfos(data.sticker_infos_style ?? {});
        setCoverInfos(data.cover_infos_style ?? {});
        const loadedCoverName = data.cover_name ?? '';
        console.log('[BookLoader] cover_name from API:', loadedCoverName, '| raw keys:', Object.keys(data).filter(k => k.includes('cover')));
        setCoverName(loadedCoverName);
        setFavoritesColors(data.favorites_colors ?? []);
        setSocialNetworkName(data.social_network_name ?? null);
        setPictureToUse(new Set(data.picture_to_use ?? []));
        setAllFolders((data.all_folders ?? []).map((f: any) => ({ id: f.id, title: f.title ?? f.name ?? '', pictures_count: f.pictures_count ?? 0 })));
        setYearbookQuantity(data.yearbook_quantity ?? 0);
        setWithTrombi(data.with_trombi !== false);
        setPlateforme(data.plateforme ?? '');
        setYearbookId(data.yearbook_id ?? null);
        setDefaultFont(data.default_font ?? 'DM Sans');
        // Lock all pages whose pageNumber is NOT in only_page_id (when provided & non-empty)
        const onlyIds = Array.isArray(data.only_page_id) ? data.only_page_id : [];
        setOnlyPageIds(onlyIds);
        setRole(data.role ?? '');
        setFormatDisplay(data.format_diplay ?? '');
        setPriceWithPromoDisplay(data.price_with_promo_display ?? '');
        setPriceWithoutPromoDisplay(data.price_without_promo_display ?? '');
        setFormatOfYearbook(data.format_of_yearbook ?? '');
        setCurrentFormat(data.current_format ?? data.format_of_yearbook ?? '');
        setSocialNetworkId(data.social_network_id ?? null);
        setUploadSocialNetworkId(data.social_network_id);
        setPicturesCount(typeof data.pictures_count === 'number' ? data.pictures_count : null);
        if (onlyIds.length > 0) {
          const allowed = new Set(onlyIds);
          const locked = new Set<number>();
          for (const p of allPages) {
            if (!allowed.has(p.pageNumber)) locked.add(p.pageNumber);
          }
          setLockedPageNumbers(locked);
        } else {
          setLockedPageNumbers(new Set());
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.warn('[BookLoader] Failed to load book, using fallback:', err);
        const fallback = Array.from({ length: FALLBACK_PAGE_COUNT }, (_, i) =>
          createEmptyPage(i + 1, i + 1)
        );
        setBookPages(fallback);
        setInitialLoadedIndices(new Set());
        setIsLoading(false);
      });
  }, []);

  return { bookPages, initialLoadedIndices, trameTemplateInfos, trameBackgroundInfos, stickerInfos, coverInfos, coverName, favoritesColors, socialNetworkName, pictureToUse, allFolders, yearbookQuantity, withTrombi, plateforme, yearbookId, defaultFont, lockedPageNumbers, onlyPageIds, role, formatDisplay, priceWithPromoDisplay, priceWithoutPromoDisplay, formatOfYearbook, currentFormat, socialNetworkId, picturesCount, isLoading };
}